import { FC, useState, useMemo, useEffect } from "react";
import {
  CheckSquare,
  Square,
  Clock,
  Truck,
  CheckCircle2,
  XCircle,
  ExternalLink,
} from "lucide-react";
import { TVessel } from "~/types";
import { ServiceType } from "~/utils/constants";
import { capitalize, formatFullAddress } from "~/utils";
import dayjs from "dayjs";
import { useCheckInventory, useBulkCheckInventory } from "~/services/inventory";
import { useNotification } from "~/hooks";
import { Modal, Select, Tag, Tabs } from "antd";
import { useNavigate } from "react-router-dom";
import UpcomingServiceTickets from "../../inventories/list-inventories/upcoming-service-tickets";
import HistoryServiceTickets from "../../inventories/list-inventories/history-service-tickets";
import { useAuth } from "~/store/auth.store";

type InventoryStatus = 'pending' | 'confirmed' | 'removed';

type InventoryConfirmation = {
  status: InventoryStatus;
  confirmedBy?: string;
  confirmedAt?: string;
};

type Props = {
  vessel: TVessel;
};

export const VesselLoadListCard: FC<Props> = ({ vessel }) => {
  const [checkedInventories, setCheckedInventories] = useState<Record<string, boolean>>({});
  const [inventoryConfirmations, setInventoryConfirmations] = useState<Record<string, InventoryConfirmation>>({});
  const [checkedSupplies, setCheckedSupplies] = useState<Record<string, boolean>>({});
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [selectedServiceId, setSelectedServiceId] = useState<string | null>(null);
  const [bulkStatus, setBulkStatus] = useState<InventoryStatus>('confirmed');
  const [inventoryModalOpen, setInventoryModalOpen] = useState(false);
  const [selectedInventory, setSelectedInventory] = useState<any | null>(null);
  const { mutate: checkInventory } = useCheckInventory();
  const { mutate: bulkCheckInventory } = useBulkCheckInventory();
  const { success, error: showError } = useNotification();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Get user initials from first letter of first name and last name
  const getUserInitials = () => {
    if (!user) return "U"; // Unknown user

    const firstName = user.firstName || "";
    const lastName = user.lastName || "";

    if (firstName && lastName) {
      return `${firstName.charAt(0).toUpperCase()}${lastName.charAt(0).toUpperCase()}`;
    } else if (firstName) {
      return firstName.charAt(0).toUpperCase();
    } else if (lastName) {
      return lastName.charAt(0).toUpperCase();
    }

    return "U"; // Fallback if no name available
  };

  const handleServiceClick = (service: any) => {
    const bookingId = service.jobTicket?.id;
    if (bookingId) {
      navigate(`/bookings/${bookingId}?tab=services&serviceId=${service.id}`);
    }
  };

  const handleInventoryClick = (inventory: any) => {
    setSelectedInventory(inventory);
    setInventoryModalOpen(true);
  };

  // Calculate totals
  const { totalPackages, totalCubicFeet } = useMemo(() => {
    let totalPkgs = 0;
    let totalCubicFt = 0;

    vessel.serviceTickets?.forEach((service) => {
      const availableInventories = service.inventories?.filter(inv => 
        !inv.status || inv.status === 'available' || inv.status === 'availableonhand' || inv.status === ''
      ) || [];
      
      totalPkgs += availableInventories.length;
      
      availableInventories.forEach((inventory) => {
        if (inventory.length && inventory.width && inventory.height) {
          totalCubicFt += (Number(inventory.length) * Number(inventory.width) * Number(inventory.height)) / 1728;
        }
      });
    });

    return { totalPackages: totalPkgs, totalCubicFeet: totalCubicFt };
  }, [vessel.serviceTickets]);

  // Calculate if all inventory items are confirmed
  const allInventoriesConfirmed = useMemo(() => {
    const filteredServices = vessel.serviceTickets?.filter(service => {
      if (service.serviceType === ServiceType.DELIVERY) return true;
      if (service.serviceType === ServiceType.COLLECTION) {
        return service.supplyItems && service.supplyItems.length > 0;
      }
      return false;
    }) || [];

    const allInventoryIds: string[] = [];
    filteredServices.forEach((service) => {
      const availableInventories = service.inventories?.filter(inv =>
        !inv.status || inv.status === 'available' || inv.status === 'availableonhand' || inv.status === ''
      ) || [];
      availableInventories.forEach(inv => allInventoryIds.push(inv.id));
    });

    if (allInventoryIds.length === 0) return false;

    return allInventoryIds.every(id => inventoryConfirmations[id]?.status === 'confirmed');
  }, [vessel.serviceTickets, inventoryConfirmations]);

  // Load state from localStorage
  useEffect(() => {
    vessel.serviceTickets?.forEach((service) => {
      try {
        const savedChecks = localStorage.getItem(`service-${service.id}-inventories-checks`);
        if (savedChecks) {
          const parsed = JSON.parse(savedChecks);
          setCheckedInventories(prev => ({ ...prev, ...parsed }));
        }

        const savedConfirmations = localStorage.getItem(`service-${service.id}-inventories-confirmations`);
        if (savedConfirmations) {
          const parsed = JSON.parse(savedConfirmations);
          setInventoryConfirmations(prev => ({ ...prev, ...parsed }));
        }

        const savedSupplies = localStorage.getItem(`service-${service.id}-supplies-checks`);
        if (savedSupplies) {
          const parsed = JSON.parse(savedSupplies);
          setCheckedSupplies(prev => ({ ...prev, ...parsed }));
        }
      } catch (e) {
        console.error("Error loading check state from localStorage:", e);
      }
    });
  }, [vessel.serviceTickets]);

  // Handle bulk confirmation
  const handleBulkConfirm = (serviceId: string, availableInventories: any[]) => {
    const checkedIds = availableInventories
      .map(inv => inv.id)
      .filter(id => checkedInventories[id]);

    if (checkedIds.length === 0) {
      showError({
        message: "No items selected",
        description: "Please select at least one item to confirm",
      });
      return;
    }

    setSelectedServiceId(serviceId);
    setConfirmModalOpen(true);
  };

  // Get count of selected items for current service
  const getSelectedItemCount = () => {
    if (!selectedServiceId) return 0;

    const service = vessel.serviceTickets?.find(s => s.id === selectedServiceId);
    if (!service) return 0;

    const availableInventories = service.inventories?.filter(inv =>
      !inv.status || inv.status === 'available' || inv.status === 'availableonhand' || inv.status === ''
    ) || [];

    return availableInventories.filter(inv => checkedInventories[inv.id]).length;
  };

  const handleConfirmModal = () => {
    if (!selectedServiceId) return;

    const service = vessel.serviceTickets?.find(s => s.id === selectedServiceId);
    if (!service) return;

    const availableInventories = service.inventories?.filter(inv =>
      !inv.status || inv.status === 'available' || inv.status === 'availableonhand' || inv.status === ''
    ) || [];

    const checkedIds = availableInventories
      .map(inv => inv.id)
      .filter(id => checkedInventories[id]);

    const timestamp = dayjs().format('M/D/YY, HH:mm');
    const initials = getUserInitials();

    const updatedConfirmations = { ...inventoryConfirmations };
    checkedIds.forEach(id => {
      updatedConfirmations[id] = {
        status: bulkStatus,
        confirmedBy: bulkStatus === 'confirmed' ? initials : undefined,
        confirmedAt: bulkStatus === 'confirmed' ? timestamp : undefined,
      };
    });

    setInventoryConfirmations(updatedConfirmations);

    try {
      localStorage.setItem(
        `service-${selectedServiceId}-inventories-confirmations`,
        JSON.stringify(updatedConfirmations)
      );
    } catch (e) {
      console.error("Error saving confirmations:", e);
    }

    // Call backend for each item
    checkedIds.forEach(inventoryId => {
      const checked = bulkStatus === 'confirmed';
      checkInventory({
        inventoryId,
        checked,
        serviceTicketId: selectedServiceId,
        timestamp: new Date().toISOString(),
      }, {
        onError: (err) => {
          showError({
            message: "Operation failed",
            description: "Failed to update some inventory items",
          });
          console.error("Error updating inventory:", err);
        },
      });
    });

    const statusLabel = bulkStatus === 'confirmed' ? 'confirmed' : bulkStatus === 'removed' ? 'removed from service' : 'set to pending';
    success({
      message: "Status updated",
      description: `${checkedIds.length} item(s) ${statusLabel}`,
    });

    setConfirmModalOpen(false);
    setSelectedServiceId(null);
  };

  // Filter services based on vessel type
  const filteredServices = vessel.serviceTickets?.filter(service => {
    // For warehouse-release, show all warehouse services (already filtered for RELEASE)
    if ((vessel as any).isWarehouseRelease) {
      return service.serviceType === ServiceType.WAREHOUSE_SERVICE;
    }
    // For regular vessels, show deliveries or collections with supplies
    if (service.serviceType === ServiceType.DELIVERY) {
      return true;
    }
    if (service.serviceType === ServiceType.COLLECTION) {
      // Only show collections that have supplies
      return service.supplyItems && service.supplyItems.length > 0;
    }
    return false;
  }) || [];

  const isWarehouseRelease = (vessel as any).isWarehouseRelease;

  return (
    <div className="bg-white rounded-lg border shadow-sm">
      {/* Vessel Header */}
      <div className="p-4 border-b bg-gray-50">
        <div className="flex justify-between items-start">
          <div className="flex items-center gap-3">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {vessel.name}
              </h3>
              {!isWarehouseRelease && (
                <p className="text-sm text-gray-600">
                  {vessel.vesselRef} • {vessel.vesselType?.name}
                </p>
              )}
              {isWarehouseRelease && (
                <p className="text-sm text-gray-600">
                  Warehouse Services - Release Items
                </p>
              )}
            </div>
            {allInventoriesConfirmed && (
              <Tag color="green" className="text-base px-3 py-1">
                <CheckCircle2 size={16} className="inline mr-1" />
                LOADED
              </Tag>
            )}
          </div>
          <div className="text-right">
            <div className="text-sm text-gray-600">
              Services: {filteredServices.length}
            </div>
            <div className="text-sm text-gray-600">
              Total Packages: {totalPackages}
            </div>
            <div className="text-sm text-gray-600">
              Total Cubic Feet: {totalCubicFeet.toFixed(2)}
            </div>
          </div>
        </div>
      </div>

      {/* Services List */}
      <div className="divide-y">
        {filteredServices.map((service) => {
          const availableInventories = service.inventories?.filter(inv => 
            !inv.status || inv.status === 'available' || inv.status === 'availableonhand' || inv.status === ''
          ) || [];
          
          const bookingClient = service.jobTicket?.client;
          const serviceClient = service.client;
          
          return (
            <div key={service.id} className="p-4">
              {/* Service Header Row */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <div className="flex items-center space-x-2">
                    <span
                      className="font-medium text-gray-900 hover:text-blue-600 cursor-pointer flex items-center gap-1"
                      onClick={() => handleServiceClick(service)}
                      title="Click to view service in booking"
                    >
                      {service.serial}
                      <ExternalLink size={14} className="text-gray-400" />
                    </span>
                    <span
                      className={`px-2 py-1 rounded-full text-xs ${
                        service.serviceType === ServiceType.COLLECTION
                          ? "bg-blue-100 text-blue-800"
                          : "bg-green-100 text-green-800"
                      }`}
                    >
                      {capitalize(service.serviceType)}
                    </span>
                  </div>
                  <div className="text-sm text-gray-600">
                    {dayjs(service.startDatetime).format("HH:mm A")} - {dayjs(service.endDatetime).format("HH:mm A")}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-medium text-gray-900">
                    {availableInventories.length} packages
                  </div>
                  <div className="text-xs text-gray-500">
                    {(
                      availableInventories.reduce((total, inv) => {
                        if (inv.length && inv.width && inv.height &&
                            Number(inv.length) > 0 && Number(inv.width) > 0 && Number(inv.height) > 0) {
                          return total + (Number(inv.length) * Number(inv.width) * Number(inv.height)) / 1728;
                        }
                        return total;
                      }, 0)
                    ).toFixed(2)} cu ft
                  </div>
                </div>
              </div>

              {/* Service Details Row */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3 text-sm">
                <div>
                  <div className="text-xs text-gray-500 font-medium">Booking Client</div>
                  <div className="text-gray-900">
                    {bookingClient?.companyName || 
                     `${bookingClient?.firstName || ''} ${bookingClient?.lastName || ''}`.trim() || 
                     'N/A'}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 font-medium">Service Client</div>
                  <div className="text-gray-900">
                    {serviceClient?.companyName || 
                     `${serviceClient?.firstName || ''} ${serviceClient?.lastName || ''}`.trim() || 
                     'N/A'}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 font-medium">Address</div>
                  <div className="text-gray-900">
                    {formatFullAddress(service.location)}
                  </div>
                </div>
              </div>

              {/* Inventory Items */}
              {availableInventories.length > 0 && (
                <div className="border-t pt-3">
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-sm font-medium text-gray-900">Inventory Items</div>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            const allIds = availableInventories.map(inv => inv.id);
                            const updatedChecks = { ...checkedInventories };
                            allIds.forEach(id => {
                              updatedChecks[id] = true;
                            });
                            setCheckedInventories(updatedChecks);
                            try {
                              localStorage.setItem(
                                `service-${service.id}-inventories-checks`,
                                JSON.stringify(updatedChecks)
                              );
                            } catch (e) {
                              console.error("Error saving check state:", e);
                            }
                          }}
                          className="text-xs text-blue-600 hover:text-blue-800 underline"
                        >
                          Select All
                        </button>
                        <span className="text-gray-300">|</span>
                        <button
                          onClick={() => {
                            const allIds = availableInventories.map(inv => inv.id);
                            const updatedChecks = { ...checkedInventories };
                            allIds.forEach(id => {
                              updatedChecks[id] = false;
                            });
                            setCheckedInventories(updatedChecks);
                            try {
                              localStorage.setItem(
                                `service-${service.id}-inventories-checks`,
                                JSON.stringify(updatedChecks)
                              );
                            } catch (e) {
                              console.error("Error saving check state:", e);
                            }
                          }}
                          className="text-xs text-blue-600 hover:text-blue-800 underline"
                        >
                          Select None
                        </button>
                      </div>
                      <div className="flex items-center gap-2">
                        <Select
                          value={bulkStatus}
                          onChange={(value: InventoryStatus) => setBulkStatus(value)}
                          style={{ width: 130 }}
                          size="small"
                        >
                          <Select.Option value="pending">
                            <span className="text-orange-600">Pending</span>
                          </Select.Option>
                          <Select.Option value="confirmed">
                            <span className="text-green-600">Confirmed</span>
                          </Select.Option>
                          <Select.Option value="removed">
                            <span className="text-red-600">Removed</span>
                          </Select.Option>
                        </Select>
                        <button
                          onClick={() => handleBulkConfirm(service.id, availableInventories)}
                          className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
                        >
                          Apply
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-1">
                    {availableInventories.map((item) => {
                      const confirmation = inventoryConfirmations[item.id];
                      const isChecked = checkedInventories[item.id] || false;
                      return (
                      <div
                        key={item.id}
                        className="flex items-center justify-between p-2 bg-gray-50 rounded border"
                      >
                        <div className="flex-1">
                          <div className="flex items-center space-x-3">
                            <div
                              className="w-5 h-5 border-2 border-gray-300 rounded cursor-pointer hover:bg-gray-200 flex items-center justify-center"
                              onClick={() => {
                                const newChecked = !isChecked;
                                setCheckedInventories((prev) => {
                                  const updatedState = { ...prev, [item.id]: newChecked };
                                  try {
                                    localStorage.setItem(
                                      `service-${service.id}-inventories-checks`,
                                      JSON.stringify(updatedState)
                                    );
                                  } catch (e) {
                                    console.error("Error saving check state:", e);
                                  }
                                  return updatedState;
                                });
                              }}
                            >
                              {isChecked ? (
                                <CheckSquare size={16} className="text-blue-600" />
                              ) : (
                                <Square size={16} className="text-gray-400" />
                              )}
                            </div>
                            <div
                              className="flex-1 cursor-pointer hover:text-blue-600"
                              onClick={() => handleInventoryClick(item)}
                              title="Click to view inventory details"
                            >
                              <div className="text-sm font-medium text-gray-900 hover:text-blue-600 flex items-center gap-1">
                                {item.title || item.inventoryRef || "Untitled Item"}
                                <ExternalLink size={12} className="text-gray-400" />
                              </div>
                              <div className="text-xs text-gray-500">
                                {item.clientInternalRef && `Ref: ${item.clientInternalRef}`}
                                {item.artistName && ` • Artist: ${item.artistName}`}
                                {item.length && item.width && item.height &&
                                 ` • ${item.length}" × ${item.width}" × ${item.height}"`}
                                {item.weight && ` • ${item.weight} lbs`}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-right">
                            <div className="text-xs text-gray-500">
                              {item.containerType || item.packingType || item.itemType || "N/A"}
                            </div>
                            {item.pieceCount && (
                              <div className="text-xs text-gray-500">{item.pieceCount} pieces</div>
                            )}
                          </div>
                          {confirmation && (
                            <div className="text-right min-w-[120px]">
                              <div>
                                {confirmation.status === 'confirmed' && (
                                  <Tag color="green">Confirmed</Tag>
                                )}
                                {confirmation.status === 'removed' && (
                                  <Tag color="red">Removed</Tag>
                                )}
                                {confirmation.status === 'pending' && (
                                  <Tag color="orange">Pending</Tag>
                                )}
                              </div>
                              {confirmation.confirmedBy && confirmation.confirmedAt && (
                                <div className="text-xs text-gray-500 mt-1">
                                  {confirmation.confirmedBy} • {confirmation.confirmedAt}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Supplies (for collections) */}
              {service.serviceType === ServiceType.COLLECTION && service.supplyItems && service.supplyItems.length > 0 && (
                <div className="border-t pt-3">
                  <div className="text-sm font-medium text-gray-900 mb-2">Supplies</div>
                  <div className="space-y-1">
                    {service.supplyItems.map((supplyItem) => (
                      <div
                        key={supplyItem.id}
                        className="flex items-center justify-between p-2 bg-gray-50 rounded border"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-4 h-4 border border-gray-300 rounded cursor-pointer hover:bg-gray-200 flex items-center justify-center"
                               onClick={() => {
                                 const isChecked = !checkedSupplies[supplyItem.id];
                                 setCheckedSupplies((prev) => {
                                   const updatedState = { ...prev, [supplyItem.id]: isChecked };
                                   try {
                                     localStorage.setItem(
                                       `service-${service.id}-supplies-checks`,
                                       JSON.stringify(updatedState)
                                     );
                                   } catch (e) {
                                       console.error("Error saving check state:", e);
                                   }
                                   return updatedState;
                                 });
                               }}>
                            {checkedSupplies[supplyItem.id] ? (
                              <CheckSquare size={12} className="text-blue-600" />
                            ) : (
                              <Square size={12} className="text-gray-400" />
                            )}
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {supplyItem.supply?.name || "Unknown Supply"}
                            </div>
                            <div className="text-xs text-gray-500">
                              Quantity: {supplyItem.quantity}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Confirmation Modal */}
      <Modal
        title={`Confirm ${bulkStatus === 'confirmed' ? 'Load' : bulkStatus === 'removed' ? 'Removal' : 'Pending Status'}`}
        open={confirmModalOpen}
        onOk={handleConfirmModal}
        onCancel={() => {
          setConfirmModalOpen(false);
          setSelectedServiceId(null);
        }}
        okText="Confirm"
        okButtonProps={{ type: 'primary' }}
      >
        <p>
          {bulkStatus === 'confirmed' && `Are you sure the (${getSelectedItemCount()}) selected item${getSelectedItemCount() !== 1 ? 's' : ''} have been loaded onto the vessel?`}
          {bulkStatus === 'removed' && `Are you sure you want to mark the (${getSelectedItemCount()}) selected item${getSelectedItemCount() !== 1 ? 's' : ''} as removed from service?`}
          {bulkStatus === 'pending' && `Are you sure you want to mark the (${getSelectedItemCount()}) selected item${getSelectedItemCount() !== 1 ? 's' : ''} as pending?`}
        </p>
        <p className="text-sm text-gray-500 mt-2">
          This action will update all selected inventory items.
        </p>
      </Modal>

      {/* Inventory Details Modal */}
      <Modal
        title={
          <div>
            <div className="text-lg font-semibold">
              {selectedInventory?.title || selectedInventory?.inventoryRef || "Inventory Details"}
            </div>
            <div className="text-sm text-gray-500 font-normal">
              {selectedInventory?.clientInternalRef && `Ref: ${selectedInventory.clientInternalRef}`}
            </div>
          </div>
        }
        open={inventoryModalOpen}
        onCancel={() => {
          setInventoryModalOpen(false);
          setSelectedInventory(null);
        }}
        footer={null}
        width={900}
      >
        {selectedInventory && (
          <Tabs
            defaultActiveKey="details"
            items={[
              {
                key: 'details',
                label: 'Details',
                children: (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-xs text-gray-500">Artist</div>
                        <div className="text-sm font-medium">{selectedInventory.artistName || 'N/A'}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">Medium</div>
                        <div className="text-sm font-medium">{selectedInventory.medium || 'N/A'}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">Dimensions</div>
                        <div className="text-sm font-medium">
                          {selectedInventory.length && selectedInventory.width && selectedInventory.height
                            ? `${selectedInventory.length}" × ${selectedInventory.width}" × ${selectedInventory.height}"`
                            : 'N/A'}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">Weight</div>
                        <div className="text-sm font-medium">{selectedInventory.weight ? `${selectedInventory.weight} lbs` : 'N/A'}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">Packing Type</div>
                        <div className="text-sm font-medium">
                          {selectedInventory.containerType || selectedInventory.packingType || selectedInventory.itemType || 'N/A'}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">Value</div>
                        <div className="text-sm font-medium">
                          {selectedInventory.value ? `$${selectedInventory.value.toLocaleString()}` : 'N/A'}
                        </div>
                      </div>
                    </div>
                  </div>
                ),
              },
              {
                key: 'upcoming',
                label: 'Upcoming Service Tickets',
                children: <UpcomingServiceTickets inventoryId={selectedInventory.id} />,
              },
              {
                key: 'history',
                label: 'History',
                children: <HistoryServiceTickets inventoryId={selectedInventory.id} />,
              },
            ]}
          />
        )}
      </Modal>
    </div>
  );
};
